import React, { useState } from 'react'
import { Box, Stack, Link, Divider } from '@mui/material'
import { useParams, Link as RouterLink } from 'react-router-dom'

import { EmptyState } from '../../../../common/components/EmptyState'
import { useTranslation } from '../../../../common/hooks/helper/useTranslation'
import { Loading } from '../../../../common/components/Loading'
import { PatientHeadlineInfo } from './PatientHeadlineInfo'
import { PatientTabNavigation } from './PatientTabNavigation'
import { PatientPanel } from './PatientPanel'
import { useDoctorPatientProfileData } from '../hooks/useDoctorPatientProfileData'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useUserType } from '../../../../common/hooks'
import { DoctorPatientDetails } from './DoctorPatientDetails'
import { Alert } from '../../../../common/components/Alert/Alert'
import { useEesztTokenState } from '../../../../common/hooks/eeszt/useEesztTokenState'
import { DoctorPatientRouteParams } from '../types/doctorPatients.types'

const DoctorPatient: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { patientId, treatmentId } = useParams<DoctorPatientRouteParams>()
  const { t } = useTranslation()

  const userType = useUserType()
  const isEesztTokenValid = useEesztTokenState()

  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const {
    patientProfile,
    loading,
    refetch: refetchPatientProfile,
  } = useDoctorPatientProfileData({
    skip: false,
  })

  return (
    <Box
      flexGrow={1}
      height="100%"
      display="flex"
      flexDirection="column"
      sx={{ backgroundColor: 'grey.200' }}
    >
      {loading && <Loading />}
      {!loading && !patientProfile && (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyState
            title={t('patients:patientNotFoundTitle')}
            description={t('patients:patientNotFoundMessage')}
          />
        </Box>
      )}
      {!loading && patientProfile && (
        <>
          <Alert
            title={t('treatment:EESZTLoginNeeded')}
            variant="warning"
            showAlert={
              !isEesztTokenValid && activeTabIndex !== 2 && activeTabIndex !== 6
            }
          />
          {!treatmentId && (
            <>
              <Stack direction="row" alignItems="flex-start">
                <PatientHeadlineInfo patient={patientProfile} />
                <PatientTabNavigation
                  patientId={patientId}
                  activeTabIndex={activeTabIndex}
                  setActiveTabIndex={setActiveTabIndex}
                />
              </Stack>
              <Link
                underline="none"
                component={RouterLink}
                to={`/${userType}/patients`}
                display="flex"
                alignItems="center"
                marginLeft={2}
                width="fit-content"
              >
                <KeyboardArrowLeftIcon fontSize="large" />
                {t('treatment:backToPatiens')}
              </Link>
            </>
          )}

          <Divider />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {!treatmentId && (
              <DoctorPatientDetails
                patientProfile={patientProfile}
                refetchPatientProfile={refetchPatientProfile}
                setActiveTabIndex={setActiveTabIndex}
              />
            )}
            <PatientPanel
              activeTabIndex={activeTabIndex}
              patientProfile={patientProfile}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export { DoctorPatient }
